import React, { useState, useEffect } from 'react';
import logger from '../../utils/logger';
import apiService from '../../services/api';
import ValidationJobsPanel from './ValidationJobsPanel';

interface ProspectsUploadProps {
  onUploadSuccess: () => void;
}

interface ClientOption { value: string; label: string }

const ProspectsUpload: React.FC<ProspectsUploadProps> = ({ onUploadSuccess }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [clientsLoading, setClientsLoading] = useState<boolean>(false);
  const [clientsError, setClientsError] = useState<string | null>(null);
  const [selectedClient, setSelectedClient] = useState(''); // empty = not chosen
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationRefreshToken, setValidationRefreshToken] = useState(0);

  // Fetch clients list
  useEffect(() => {
    const loadClients = async () => {
      setClientsLoading(true);
      try {
        const data = await apiService.getClients();
        if (Array.isArray(data)) {
          // Handle both string array and object array formats
          const dynamicOptions: ClientOption[] = data.map((c: any) => {
            if (typeof c === 'string') {
              // API returns string array
              return { value: c, label: c };
            } else {
              // API returns full Client objects
              return { value: c.account, label: c.client_name || c.account };
            }
          });
          setClients(dynamicOptions);
          // If previously selected client no longer exists, reset to empty
          if (!dynamicOptions.find(o => o.value === selectedClient)) {
            setSelectedClient('');
          }
          setClientsError(dynamicOptions.length === 0 ? 'No clients available. Create a client first.' : null);
        } else {
          setClientsError('Unexpected clients response format');
        }
      } catch (e: any) {
        logger.error('Failed to load clients list:', e);
        setClientsError('Failed to load clients');
      } finally {
        setClientsLoading(false);
      }
    };
    loadClients();
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    setMessage(null);
    
    // ✅ Validate file size before upload (100MB limit)
    if (file && file.size > 100 * 1024 * 1024) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      setMessage({ 
        text: `File too large. Maximum size is 100MB. Your file is ${fileSizeMB}MB`, 
        type: 'error' 
      });
      setSelectedFile(null);
      event.target.value = ''; // Reset input
    }
  };

  const handleClientChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedClient(event.target.value);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedClient) {
      setMessage({ text: 'Please select a client account', type: 'error' });
      return;
    }
    if (!selectedFile) {
      setMessage({ text: 'Please select a file to upload', type: 'error' });
      return;
    }

    setIsUploading(true);
    setMessage(null);

  const formData = new FormData();
  formData.append('file', selectedFile);
  // Use new canonical field name 'account'. Backend still supports legacy client_name
  formData.append('account', selectedClient);

    try {
      // Use async upload to prevent timeouts with large files
      const response = await apiService.uploadProspectsCSVAsync(formData);
      if (response.success && response.upload_id) {
        // Start polling for upload status
        pollUploadStatus(response.upload_id);
      } else {
        setMessage({ text: response.message || 'Upload failed', type: 'error' });
        setIsUploading(false);
      }
    } catch (error) {
      logger.error('Upload error:', error);
      setMessage({ text: 'Upload failed. Please try again.', type: 'error' });
      setIsUploading(false);
    }
  };

  const pollUploadStatus = async (uploadId: string) => {
    try {
      const statusResponse = await apiService.getUploadStatus(uploadId);
      logger.debug('Upload status response:', statusResponse);

      if (statusResponse.status === 'completed') {
        setMessage({ text: statusResponse.message || 'File uploaded successfully!', type: 'success' });
        onUploadSuccess();
        setSelectedFile(null);
        // Reset file input
        const fileInput = document.getElementById('file-input') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
        setIsUploading(false);
      } else if (statusResponse.status === 'failed') {
        setMessage({ text: statusResponse.message || 'Upload failed', type: 'error' });
        setIsUploading(false);
      } else {
        // Still processing, continue polling
        setMessage({ text: statusResponse.message || 'Processing upload...', type: 'success' });
        setTimeout(() => pollUploadStatus(uploadId), 2000); // Poll every 2 seconds
      }
    } catch (error) {
      logger.error('Status check error:', error);
      // Add more detailed error logging
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        logger.error('Status check error response:', axiosError.response?.status, axiosError.response?.data);
      }
      setMessage({ text: 'Upload status check failed', type: 'error' });
      setIsUploading(false);
    }
  };

  const handleValidateEmails = async () => {
    logger.debug('Starting email validation...');
    setIsValidating(true);
    setMessage(null);

    try {
      logger.debug('Calling apiService.validateEmails()...');
      // ✅ Pass the selected client account for validation
      const response = await apiService.validateEmails(selectedClient || undefined);
      logger.debug('Email validation response:', response);
      
      if (response.success) {
        const job = response.job;
        if (!job) {
          setMessage({
            text: response.message || 'No emails pending validation.',
            type: 'success',
          });
          return;
        }
        const queuedCount = job?.requested_count ?? 0;
        const limitNote = response.limit_applied ? 'Only a subset was scheduled due to the configured limit.' : '';
        const scopedAccount = job?.account || (job?.account_scope === 'accessible' ? 'accessible accounts' : 'all accounts');
        setMessage({
          text: `Validation job queued for ${queuedCount} prospects (${scopedAccount}). ${limitNote}`.trim(),
          type: 'success',
        });
        setValidationRefreshToken((token) => token + 1);
      } else {
        logger.error('Email validation failed:', response.error);
        setMessage({ text: response.error || response.message || 'Email validation failed', type: 'error' });
      }
    } catch (error) {
      logger.error('Email validation error:', error);
      setMessage({ text: 'Email validation failed. Please try again.', type: 'error' });
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <div className="prospects-container">
        
      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}
      
      <div className="upload-section">
        <h1>Upload Prospects CSV</h1>
        <form onSubmit={handleSubmit} className="upload-form">
          <div className="form-group">
            <label htmlFor="client-select">Select Client:</label>
            <select
              id="client-select"
              value={selectedClient}
              onChange={handleClientChange}
              disabled={isUploading}
            >
              <option value="" disabled>Select One</option>
              {clients.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            {clientsLoading && <div style={{ fontSize: '0.8rem' }}>Loading clients...</div>}
            {clientsError && <div style={{ fontSize: '0.8rem', color: 'red' }}>{clientsError}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="file-input">Choose CSV File:</label>
            <input 
              type="file" 
              id="file-input"
              accept=".csv"
              onChange={handleFileChange}
              disabled={isUploading}
            />
          </div>

          <div className="button-group">
            <button 
              type="submit" 
              className="wizrdom-button accent"
              disabled={!selectedFile || !selectedClient || isUploading || isValidating}
            >
              <span className="elementor-button-content-wrapper">
                <span className="elementor-button-text">
                  {isUploading ? 'Uploading...' : 'Upload CSV'}
                </span>
              </span>
            </button>
          </div>
        </form>
      </div>

      <div className="upload-section">
        <h1>Email Status Management</h1>
        <div className="button-group">
          <button 
            onClick={handleValidateEmails}
            className="wizrdom-button accent"
            disabled={isUploading || isValidating}
          >
            <span className="elementor-button-content-wrapper">
              <span className="elementor-button-text">
                {isValidating ? 'Validating...' : 'Validate Emails'}
              </span>
            </span>
          </button>
        </div>
      </div>

      <ValidationJobsPanel refreshToken={validationRefreshToken} />
    </div>
  );
};

export default ProspectsUpload;
